import { ImageObject } from '~/engine/data/image/ImageObject';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { PanZoom } from '../../../view/PanZoom';
import { LineDrawActionState } from '../../actions/contouring-actions/LineDrawAction';
import { DrawingTool } from './DrawingTool';
import { TargetCrosshair } from '~/engine/data/common/customs/TargetCrosshair';
import { Orientations, PlaneView } from '../../../view/views/PlaneView';
import { ModifierKeys } from '~/engine/tool/ModifierKeys';
import { ReadonlyVec2 } from 'gl-matrix';
import { View } from '~/engine/view/View';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '~/engine/tool/EventHandlerResult';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { ViewManager } from '~/engine/view/ViewManager';
import { enqueueSnackbar } from 'notistack';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomstructureset';

export class LineDrawTool extends DrawingTool {
    public readonly toolType = 'line';
    private _cursorAction?: TargetCrosshair;
    private readonly image: ImageObject;
    private readonly structure: RegionOfInterest;
    private readonly isErasing: boolean;
    protected readonly _planeView: PlaneView;
    protected _drawState?: LineDrawActionState;

    public constructor(
        image: ImageObject,
        structure: RegionOfInterest,
        panZoom: PanZoom,
        mode: 'draw' | 'erase',
        view: PlaneView
    ) {
        super(panZoom);
        this.image = image;
        this.structure = structure;
        this.isErasing = mode === 'erase';
        this._planeView = view;
        this.initializeCursor();
    }

    private initializeCursor() {
        if (
            this._planeView instanceof PlaneView &&
            !this.structure.dicomParent.isApproved &&
            this._planeView.label === Orientations.TRANSVERSAL_ORIENTATION
        ) {
            this._cursorAction = new TargetCrosshair(
                this._planeView,
                this.isErasing,
                true
            );
        }
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: View
    ): EventHandlerResult {
        if (this._cursorAction) {
            this._cursorAction.setCustomCursor();
        } else {
            this.initializeCursor();
        }

        if (this._drawState) {
            return this._drawState.onMouseMove(coordinates, buttons, view);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (
            this.structure.dicomParent.approvalStatus ===
            DICOMApprovalStatus.APPROVED
        ) {
            this.resetDrawAction(view);
            enqueueSnackbar(
                'Contouring is not allowed for this ROI as it has already been reviewed.',
                { variant: 'warning' }
            );
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (
            button === 0 &&
            view.label === Orientations.TRANSVERSAL_ORIENTATION
        ) {
            view.snapToImagePixel();
            if (!this._drawState) {
                this.resetDrawAction(view);
            }

            this._drawState?.onMousePress(
                button,
                modifierKeys,
                coordinates,
                view
            );
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }

        return this._panAction.onMousePress(
            button,
            modifierKeys,
            coordinates,
            view
        );
    }

    public onMouseRelease(
        button: number,
        coordinates: ReadonlyVec2,
        view?: View
    ): EventHandlerResult {
        if (this._drawState) {
            const result = this._drawState.onMouseRelease(
                button,
                coordinates,
                view
            );
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.structure.boundingBox,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
            return result;
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseWheel(
        delta: 1 | -1,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (this._drawState) {
            const actionState = this._drawState as LineDrawActionState;
            if (actionState.drawing && !modifierKeys.hasCtrl) {
                actionState.discardDrawing();
            }
        }
        return this._zoomAction.onMouseWheel(
            delta,
            modifierKeys,
            coordinates,
            view
        );
    }

    public onMouseDoubleClick(
        coordinates: ReadonlyVec2,
        _view: PlaneView
    ): EventHandlerResult {
        if (this._drawState) {
            return this._drawState.finishOnPoint(coordinates);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    protected resetDrawAction(view: View) {
        if (view instanceof PlaneView) {
            this._drawState = new LineDrawActionState(
                view,
                this.structure,
                this.image,
                10,
                0,
                1,
                this.isErasing
            );
        }
    }

    public onDeactivate(): EventHandlerResult {
        let result: EventHandlerResult | undefined;
        if (this._drawState) {
            result = this._drawState.stopAction();
            this._drawState = undefined;
        }
        this._cursorAction?.stopAction();
        this._cursorAction?.resetCursor();
        this._cursorAction = undefined;

        return (
            result ??
            new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED)
        );
    }
}
